<template>
  <TableComp
    :name="$t('global.staging-environments')"
    :headers="headers"
    :status="status"
    :number-of-items="stagingEnvironments.length"
    empty-image="staging-environments"
    :empty-actions="getEmptyActions()"
  >
    <template #desktop>
      <tr v-for="stagingEnvironment in stagingEnvironments" :key="stagingEnvironment.id">
        <td>
          <router-link v-if="stagingEnvironment.isStable" :to="getRouterLink(stagingEnvironment)">
            {{ getDomain(stagingEnvironment) }}
          </router-link>
          <span v-else>{{ getDomain(stagingEnvironment) }}</span>
        </td>
        <td class="text-center">
          <a
            v-if="stagingEnvironment.isStable"
            :href="`https://${getTempDomain(stagingEnvironment)}`"
            target="_blank"
          >
            {{ getTempDomain(stagingEnvironment) }}
          </a>
          <span v-else>{{ getTempDomain(stagingEnvironment) }}</span>
        </td>
        <td class="text-center">{{ getIP(stagingEnvironment) }}</td>
        <td class="align-middle text-end actions">
          <div
            v-if="stagingEnvironment.isStable"
            class="d-flex justify-content-center align-items-center"
          >
            <ActionIcons :actions="getStagingEnvironmentActions(stagingEnvironment)" />
          </div>
          <div v-else class="d-flex justify-content-center align-items-center">
            <div class="mr-2">
              <LoadingState :size="20" />
            </div>
            <div class="text-capitalize">{{ stagingEnvironment.state }}...</div>
          </div>
        </td>
      </tr>
    </template>
    <template #mobile>
      <TableMobileRow
        v-for="stagingEnvironment in stagingEnvironments"
        :key="stagingEnvironment.id"
      >
        <template #header>
          <div class="d-flex align-items-center">
            <div class="flex-fill">
              <div class="d-flex align-items-center">
                <div class="flex-fill">{{ getDomain(stagingEnvironment) }}</div>
                <div
                  v-if="status.isLoaded || status.isUpdating"
                  class="d-flex justify-content-center"
                >
                  <ActionIcons :actions="getStagingEnvironmentActions(stagingEnvironment)" />
                </div>
                <div v-else class="d-flex justify-content-center align-items-center">
                  <LoadingState :size="20" />
                </div>
              </div>
            </div>
          </div>
        </template>
        <template #content>
          <div class="row">
            <div class="col">{{ $t("global.temp-domain") }}:</div>
            <div class="col">
              <a
                v-if="stagingEnvironment.isStable"
                :href="`https://${getTempDomain(stagingEnvironment)}`"
                target="_blank"
              >
                {{ getTempDomain(stagingEnvironment) }}
              </a>
              <span v-else>{{ getTempDomain(stagingEnvironment) }}</span>
            </div>
          </div>
          <div class="row">
            <div class="col">{{ $t("global.ip-address") }}:</div>
            <div class="col">{{ getIP(stagingEnvironment) }}</div>
          </div>
        </template>
      </TableMobileRow>
    </template>
  </TableComp>
</template>

<script>
import ActionIcons from "@/components/display/ActionIcons";
import AddStagingEnvironment from "@/components/smart/modals/DevSiteAdd";
import LoadingState from "@/components/display/LoadingState";
import NavLink from "@/classes/NavLink";
import RemoveStagingEnvironment from "@/components/smart/modals/DevSiteDelete";
import store from "@/store";
import TableComp from "@/components/display/table/TableComp";
import TableHeader from "@/classes/TableHeader";
import TableMobileRow from "@/components/display/table/TableMobileRow";

export default {
  name: "SafeHarborStagingEnvironmentsTable",
  props: {
    service: {
      type: Object,
      required: true,
    },
    stagingEnvironments: {
      type: Array,
      required: true,
    },
    status: {
      type: Object,
      required: true,
    },
    type: {
      type: String,
      required: true,
      validator: (value) => ["shared", "site"].includes(value),
    },
  },
  components: {
    ActionIcons,
    LoadingState,
    TableComp,
    TableMobileRow,
  },
  setup(props, { root }) {
    const headers = [
      new TableHeader(root.$t("global.domain")),
      new TableHeader(root.$t("global.temp-domain"), "justify-content-center"),
      new TableHeader(root.$t("global.ip-address"), "justify-content-center"),
      new TableHeader(),
    ];

    const getDomain = (stagingEnvironment) => stagingEnvironment?.cloudAccount?.domain;
    const getTempDomain = (stagingEnvironment) => stagingEnvironment?.cloudAccount?.temp_domain;
    const getIP = (stagingEnvironment) => stagingEnvironment?.cloudAccount?.ip;

    const getEmptyActions = () => [
      new NavLink(
        root.$t("global.staging-environment-create"),
        null,
        openCreateForm,
        false,
        "primaryOutline",
        null,
        props.status.isLoading
      ),
    ];

    const getRouterLink = (stagingEnvironment) => {
      if (props.type === "site") {
        return {
          name: "plan-safe-harbor-staging-environment",
          params: {
            planId: props.service.id,
            stagingEnvironmentId: stagingEnvironment.cloudAccount.id,
          },
        };
      }
      return {
        name: "shared-host-safe-harbor-staging-environment",
        params: {
          sharedHostId: props.service.id,
          stagingEnvironmentId: stagingEnvironment.cloudAccount.id,
        },
      };
    };

    const getStagingEnvironmentActions = (stagingEnvironment) => [
      new NavLink(
        root.$t("global.staging-environment-remove"),
        "trash",
        () => openDeleteForm(stagingEnvironment),
        false,
        null,
        "epsilon",
        props.status.isUpdating
      ),
    ];

    const openCreateForm = () =>
      store.dispatch("modals/open", {
        template: AddStagingEnvironment,
        data: {
          plan: props.service,
          environmentType: "staging",
          safeHarbor: props.type,
          site: props.type === "site" ? props.service.cloudAccount : null,
        },
      });

    const openDeleteForm = (stagingEnvironment) =>
      store.dispatch("modals/open", {
        template: RemoveStagingEnvironment,
        data: {
          domain: stagingEnvironment.cloudAccount.domain,
          environmentType: stagingEnvironment.environmentType,
          planId: stagingEnvironment.id,
          safeHarborServiceId: props.service.id,
        },
      });

    return {
      getDomain,
      getEmptyActions,
      getIP,
      getRouterLink,
      getStagingEnvironmentActions,
      getTempDomain,
      headers,
    };
  },
};
</script>
